import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Sparkles, Image, Type, Layers, Calendar, Clock } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

const AdminPopups = () => {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPopup, setEditingPopup] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    content_type: 'text',
    button_text: '',
    button_link: '',
    is_active: true,
    show_on_pages: 'all',
    animation_style: 'pulse',
    position: 'center',
    auto_close_seconds: 0,
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    try {
      const response = await api.get('/popup/scheduled');
      setPopups(response.data);
    } catch (error) {
      toast.error('Failed to fetch popups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPopup) {
        await api.put(`/admin/popups/${editingPopup.id}`, formData);
        toast.success('Popup updated successfully');
      } else {
        await api.post('/admin/popups', formData);
        toast.success('Popup created successfully');
      }
      setShowForm(false);
      setEditingPopup(null);
      resetForm();
      fetchPopups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save popup');
    }
  };

  const handleEdit = (popup) => {
    setEditingPopup(popup);
    setFormData({
      title: popup.title,
      content: popup.content,
      image_url: popup.image_url || '',
      content_type: popup.content_type,
      button_text: popup.button_text || '',
      button_link: popup.button_link || '',
      is_active: popup.is_active,
      show_on_pages: popup.show_on_pages,
      animation_style: popup.animation_style,
      position: popup.position,
      auto_close_seconds: popup.auto_close_seconds,
      start_date: popup.start_date ? popup.start_date.slice(0, 16) : '',
      end_date: popup.end_date ? popup.end_date.slice(0, 16) : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this popup?')) return;
    try {
      await api.delete(`/admin/popups/${id}`);
      toast.success('Popup deleted');
      fetchPopups();
    } catch (error) {
      toast.error('Failed to delete popup');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await api.post(`/admin/popups/${id}/toggle`);
      toast.success(response.data.message);
      fetchPopups();
    } catch (error) {
      toast.error('Failed to toggle popup');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      image_url: '',
      content_type: 'text',
      button_text: '',
      button_link: '',
      is_active: true,
      show_on_pages: 'all',
      animation_style: 'pulse',
      position: 'center',
      auto_close_seconds: 0,
      start_date: '',
      end_date: ''
    });
  };

  // Convert local datetime to ISO string for API
  const prepareFormData = () => {
    const data = { ...formData };
    if (data.start_date) {
      data.start_date = new Date(data.start_date).toISOString();
    } else {
      data.start_date = null;
    }
    if (data.end_date) {
      data.end_date = new Date(data.end_date).toISOString();
    } else {
      data.end_date = null;
    }
    return data;
  };

  const handleSubmitWithSchedule = async (e) => {
    e.preventDefault();
    try {
      const data = prepareFormData();
      if (editingPopup) {
        await api.put(`/admin/popups/${editingPopup.id}`, data);
        toast.success('Popup updated successfully');
      } else {
        await api.post('/admin/popups', data);
        toast.success('Popup created successfully');
      }
      setShowForm(false);
      setEditingPopup(null);
      resetForm();
      fetchPopups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save popup');
    }
  };

  const getScheduleStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-xs rounded-full">Active Now</span>;
      case 'scheduled':
        return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded-full flex items-center gap-1"><Clock className="w-3 h-3" /> Scheduled</span>;
      case 'expired':
        return <span className="px-2 py-0.5 bg-red-500/20 text-red-500 text-xs rounded-full">Expired</span>;
      case 'inactive':
        return <span className="px-2 py-0.5 bg-gray-500/20 text-gray-500 text-xs rounded-full">Inactive</span>;
      default:
        return null;
    }
  };

  const contentTypeIcon = (type) => {
    switch (type) {
      case 'text': return <Type className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      case 'gif': return <Sparkles className="w-4 h-4" />;
      case 'mixed': return <Layers className="w-4 h-4" />;
      default: return <Type className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-popups-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Attraction Popups</h1>
            <p className="text-muted-foreground">Manage eye-catching promotional popups</p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingPopup(null); resetForm(); }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-popup-btn"
          >
            <Plus className="w-5 h-5" />
            Create Popup
          </button>
        </div>

        {/* Popup Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
            <div className="glass-card p-8 max-w-2xl w-full my-8">
              <h2 className="text-2xl font-bold mb-6">
                {editingPopup ? 'Edit Popup' : 'Create New Popup'}
              </h2>
              
              <form onSubmit={handleSubmitWithSchedule} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                    placeholder="🎉 Special Offer!"
                    data-testid="popup-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Content Type</label>
                    <select
                      value={formData.content_type}
                      onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      data-testid="popup-type-select"
                    >
                      <option value="text">Text Only</option>
                      <option value="image">Image Only</option>
                      <option value="gif">GIF Only</option>
                      <option value="mixed">Text + Image/GIF</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Animation Style</label>
                    <select
                      value={formData.animation_style}
                      onChange={(e) => setFormData({ ...formData, animation_style: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      data-testid="popup-animation-select"
                    >
                      <option value="pulse">Pulse Glow</option>
                      <option value="bounce">Bounce</option>
                      <option value="shake">Shake</option>
                      <option value="glow">Glow</option>
                    </select>
                  </div>
                </div>

                {(formData.content_type === 'text' || formData.content_type === 'mixed') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Content Text *</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required={formData.content_type === 'text' || formData.content_type === 'mixed'}
                      rows={3}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      placeholder="Get 50% off on all products! Limited time offer..."
                      data-testid="popup-content-input"
                    />
                  </div>
                )}

                {(formData.content_type === 'image' || formData.content_type === 'gif' || formData.content_type === 'mixed') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Image/GIF URL *</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      required={formData.content_type !== 'text'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      placeholder="https://example.com/promo.gif"
                      data-testid="popup-image-input"
                    />
                    {formData.image_url && (
                      <div className="mt-2 p-2 bg-white/5 rounded-lg">
                        <img src={getImageUrl(formData.image_url)} alt="Preview" className="max-h-32 mx-auto rounded" />
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Button Text (Optional)</label>
                    <input
                      type="text"
                      value={formData.button_text}
                      onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      placeholder="Shop Now"
                      data-testid="popup-button-text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Button Link (Optional)</label>
                    <input
                      type="text"
                      value={formData.button_link}
                      onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      placeholder="/products or https://..."
                      data-testid="popup-button-link"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Show On Pages</label>
                    <select
                      value={formData.show_on_pages}
                      onChange={(e) => setFormData({ ...formData, show_on_pages: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      data-testid="popup-pages-select"
                    >
                      <option value="all">All Pages</option>
                      <option value="home">Homepage Only</option>
                      <option value="dashboard">Dashboard Only</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Position</label>
                    <select
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                      data-testid="popup-position-select"
                    >
                      <option value="center">Center (Modal)</option>
                      <option value="bottom-right">Bottom Right</option>
                      <option value="bottom-left">Bottom Left</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Auto Close (seconds, 0 = manual only)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.auto_close_seconds}
                    onChange={(e) => setFormData({ ...formData, auto_close_seconds: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                    data-testid="popup-autoclose-input"
                  />
                </div>

                {/* Schedule Section */}
                <div className="border border-primary/30 rounded-xl p-4 bg-primary/5">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold">Schedule (Optional)</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set start and end dates for automatic activation. Leave empty for always active.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Start Date & Time</label>
                      <input
                        type="datetime-local"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                        data-testid="popup-start-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">End Date & Time</label>
                      <input
                        type="datetime-local"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                        data-testid="popup-end-date"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded"
                    data-testid="popup-active-checkbox"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium">Active (Show to users)</label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary" data-testid="popup-submit-btn">
                    {editingPopup ? 'Update Popup' : 'Create Popup'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingPopup(null); resetForm(); }}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Popups List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : popups.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Sparkles className="w-16 h-16 text-primary/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Popups Yet</h3>
            <p className="text-muted-foreground mb-6">Create your first attraction popup to engage users</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create First Popup
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {popups.map((popup) => (
              <div
                key={popup.id}
                className={`glass-card p-6 border ${popup.schedule_status === 'active' ? 'border-primary/30' : 'border-white/10 opacity-80'}`}
                data-testid={`popup-card-${popup.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-20 h-20 flex-shrink-0 bg-white/5 rounded-xl overflow-hidden flex items-center justify-center">
                    {popup.image_url ? (
                      <img src={getImageUrl(popup.image_url)} alt={popup.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-4xl">{contentTypeIcon(popup.content_type)}</div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold truncate">{popup.title}</h3>
                      {getScheduleStatusBadge(popup.schedule_status)}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{popup.content}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 bg-white/5 rounded">{popup.content_type}</span>
                      <span className="px-2 py-1 bg-white/5 rounded">{popup.animation_style}</span>
                      <span className="px-2 py-1 bg-white/5 rounded">{popup.position}</span>
                      <span className="px-2 py-1 bg-white/5 rounded">{popup.show_on_pages}</span>
                      {popup.start_date && (
                        <span className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(popup.start_date).toLocaleDateString()}
                        </span>
                      )}
                      {popup.end_date && (
                        <span className="px-2 py-1 bg-red-500/10 text-red-400 rounded flex items-center gap-1">
                          → {new Date(popup.end_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(popup.id)}
                      className={`p-2 rounded-lg transition-all ${popup.is_active ? 'bg-green-500/20 text-green-500' : 'bg-white/5 text-muted-foreground'}`}
                      title={popup.is_active ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-popup-${popup.id}`}
                    >
                      {popup.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => handleEdit(popup)}
                      className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
                      data-testid={`edit-popup-${popup.id}`}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(popup.id)}
                      className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                      data-testid={`delete-popup-${popup.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPopups;
